/**
 * Button atom public types.
 *
 * Visual `type` is NOT the HTML button type — use `htmlType` for submit/reset/button.
 *
 * ## Why `contained` (M2) instead of `filled` (M3)
 *
 * Renaming would break every consumer. The API keeps M2-era names; map to M3
 * when auditing against Material:
 *
 * | Invirtus `type` (+ intent)     | Material Design 3           |
 * |--------------------------------|-----------------------------|
 * | `contained`                    | Filled                      |
 * | `contained` + `intent="light"` | Elevated                    |
 * | `contained` + `intent="muted"` | closest to Filled Tonal     |
 * | `outlined`                     | Outlined                    |
 * | `text`                         | Text                        |
 * | `icon`                         | Icon button                 |
 */

import type { ReactNode, ButtonHTMLAttributes } from 'react';

/** Visual appearance. See file-level M2→M3 table for Material names. */
export type ButtonType = 'contained' | 'outlined' | 'text' | 'icon';

/**
 * Semantic color intent (Invirtus tokens).
 * - primary / secondary: brand CTAs (purple / noir)
 * - light: elevated surface + shadow — must stay distinct from muted fills
 * - muted: quiet tertiary; icon is ghost at rest (state layer on interaction)
 * - semantic + *-soft: destructive / success surfaces
 */
export type ButtonIntent =
  | 'primary'
  | 'secondary'
  | 'light'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'muted'
  | 'success-soft'
  | 'danger-soft';

/** Density. Default `md` (40px visual; icon md = M3 40dp). Glyph size follows this via Button. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export type ButtonHtmlType = 'button' | 'submit' | 'reset';

export type ButtonTooltipVariant = 'default' | 'top' | 'bottom' | 'left' | 'right';

/**
 * Single source of truth for Button types that support the dashed treatment.
 * Shared by the public type constraint and the runtime style guard.
 */
export const DASHED_TYPES = ['outlined', 'icon'] as const satisfies readonly ButtonType[];

export type ButtonDashedType = (typeof DASHED_TYPES)[number];

interface ButtonBaseProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visible label. Prefer over children for standard CTAs. */
  label?: string;
  labelClassName?: string;
  /** Leading icon. */
  icon?: ReactNode;
  /** Trailing icon. */
  rightIcon?: ReactNode;
  /**
   * Brand extension — NOT Material Design 3.
   * Circular `light` plate behind the trailing icon (hero / “Book a demo”).
   */
  rightIconWithBackground?: boolean;
  /**
   * Stretch content to the button's full width: leading icon + label group on
   * the left, `rightIcon` pinned right. Only meaningful on a stretched button
   * (`className="w-full"`), e.g. a full-width nav row.
   *
   * Needed because `justify-between` in `className` lands on the `<button>`,
   * whose only flex child is the content wrapper — so it has nothing to
   * distribute. This makes the wrapper itself the flex container.
   * @default false
   */
  spreadContent?: boolean;
  intent?: ButtonIntent;
  size?: ButtonSize;
  /** In-flight: aria-busy, non-interactive, dimensions preserved. */
  loading?: boolean;
  /**
   * Toggle / selected resting state. Distinct container (`data-selected`) —
   * not `:active` and not a `type` change. When true, sets `aria-pressed`.
   * For two-state toggles, pass `aria-pressed={isOn}` so the off state is
   * announced; omit `selected` on plain CTAs.
   * @default false
   */
  selected?: boolean;
  /** Native HTML type. Default `"button"`. */
  htmlType?: ButtonHtmlType;
  /** Wraps control in Tooltip; also used as icon-button accessible title. */
  title?: string;
  tooltipVariant?: ButtonTooltipVariant;
}

/**
 * `dashed` is an add-zone modifier (pill + label, or icon square).
 * Compile-time: only on `ButtonDashedType`. Runtime: `canDash()` ignores it
 * elsewhere — defense-in-depth for dynamic `getButtonClasses` callers.
 */
export type ButtonProps = ButtonBaseProps &
  (
    | { type?: Exclude<ButtonType, ButtonDashedType>; dashed?: never }
    | {
        type: ButtonDashedType;
        /**
         * Add-zone: transparent fill, brand dashed stroke, glyph/label = dash color.
         * Atom-owned — do not pass border-dashed classNames.
         */
        dashed?: boolean;
      }
  );
